import { ref, onMounted, onBeforeUnmount } from 'vue'
import type { DeviceType } from '@/types/websocket'
import { MEDIA_QUERIES } from '@/utils/breakpoints'

const isBrowser = typeof window !== 'undefined'

export function useDevice() {
  const isDevice = ref<DeviceType>('desktop')

  if (!isBrowser) {
    return { isDevice }
  }

  const mobileQuery = window.matchMedia(MEDIA_QUERIES.mobile)
  const tabletQuery = window.matchMedia(MEDIA_QUERIES.tablet)
  const smallDesktopQuery = window.matchMedia(MEDIA_QUERIES.smallDesktop)
  const desktopQuery = window.matchMedia(MEDIA_QUERIES.desktop)

  const updateDeviceType = () => {
    if (mobileQuery.matches) {
      isDevice.value = 'mobile'
    } else if (tabletQuery.matches) {
      isDevice.value = 'tablet'
    } else if (smallDesktopQuery.matches) {
      isDevice.value = 'smallDesktop'
    } else {
      isDevice.value = 'desktop'
    }
  }

  // `number`, а не `ReturnType<typeof window.setTimeout>`: под типами node
  // (их подключает tsconfig.vitest) этот ReturnType разрешается в `Timeout`,
  // хотя сам вызов возвращает браузерный `number`.
  let debounceTimer: number | null = null
  const debouncedUpdate = () => {
    if (debounceTimer) {
      window.clearTimeout(debounceTimer)
    }
    debounceTimer = window.setTimeout(updateDeviceType, 100)
  }

  const handleMobileChange = (e: MediaQueryListEvent) => e.matches && debouncedUpdate()
  const handleTabletChange = (e: MediaQueryListEvent) => e.matches && debouncedUpdate()
  const handleDesktopChange = (e: MediaQueryListEvent) => e.matches && debouncedUpdate()
  const handleSmallDesktopChange = (e: MediaQueryListEvent) => e.matches && debouncedUpdate()

  onMounted(() => {
    updateDeviceType()
    mobileQuery.addEventListener('change', handleMobileChange)
    tabletQuery.addEventListener('change', handleTabletChange)
    smallDesktopQuery.addEventListener('change', handleSmallDesktopChange)
    desktopQuery.addEventListener('change', handleDesktopChange)
  })

  onBeforeUnmount(() => {
    mobileQuery.removeEventListener('change', handleMobileChange)
    tabletQuery.removeEventListener('change', handleTabletChange)
    desktopQuery.removeEventListener('change', handleDesktopChange)
    smallDesktopQuery.removeEventListener('change', handleSmallDesktopChange)
    if (debounceTimer) {
      window.clearTimeout(debounceTimer)
      debounceTimer = null
    }
  })

  return { isDevice }
}
